/**
 * Página Sobre Nosotros - Versión Inmersiva (Interior Page Style)
 * Diseño Awwwards para páginas interiores: elegante, funcional, con animaciones sutiles
 * 
 * Diferencias con Home:
 * - Hero más compacto (sin video, con color sólido o imagen)
 * - Animaciones más sutiles y funcionales
 * - Layout más estructurado
 * - Tipografía grande pero no gigante
 * - Transiciones entre secciones más suaves
 * 
 * Secciones:
 * 1. Hero compacto con título grande
 * 2. Intro text
 * 3. Timeline horizontal
 * 4. Split section (imagen + texto)
 * 5. Valores en grid
 * 6. Equipo
 * 7. CTA final
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import LocalizedLink from '../components/common/LocalizedLink';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import {
  AnimatedSection,
  MagneticButton,
} from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Hitos del timeline histórico de Intercasa. El texto de cada hito vive en
 * `locales/<idioma>/about.json` bajo `timeline.events.<key>`.
 */
const timelineEvents = [
  { year: '1949', key: 'foundation' },
  { year: '1973', key: 'expansion' },
  { year: '1980', key: 'export' },
  { year: '1990', key: 'innovation' },
  { year: '2014', key: 'identity' },
  { year: '2017', key: 'modernisation' },
];

/**
 * Valores de Intercasa. El texto vive en `locales/<idioma>/about.json` bajo
 * `values.items.<key>`.
 */
const valoresData = [
  { id: 1, key: 'quality', icono: '✓' },
  { id: 2, key: 'origin', icono: '◉' },
  { id: 3, key: 'tradition', icono: '♥' },
  { id: 4, key: 'closeness', icono: '◈' },
  { id: 5, key: 'innovation', icono: '✦' },
  { id: 6, key: 'safety', icono: '◆' },
];

/**
 * Página Nosotros con diseño de página interior Awwwards
 * @returns {JSX.Element} Página sobre nosotros
 */
const AboutPageImmersive = () => {
  const { t } = useTranslation('about');
  const heroRef = useRef(null);
  const timelineRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  // Animación del timeline horizontal
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;

    const items = timeline.querySelectorAll('.timeline-item');
    
    items.forEach((item, index) => {
      gsap.fromTo(item,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          delay: index * 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO SUPERIOR - Imagen de fábrica
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo oscuro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de fondo con opacidad */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: "url('/images/QUIENES SOMOS/imagen (1).webp')",
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                {t('hero.label')}
              </span>
            </AnimatedSection>

            {/* Título principal */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              <Trans
                i18nKey="hero.title"
                ns="about"
                components={{ hl: <span className="text-intercasa-yellow" /> }}
              />
            </h1>

            {/* Subtítulo */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. QUIÉNES SOMOS
        =============================================
      */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
                {t('who.title')}
              </h2>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed">
                {t('who.text')}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. TIMELINE - Diseño horizontal moderno
        =============================================
      */}
      <section className="bg-intercasa-bone py-24 md:py-32 overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <AnimatedSection animation="fadeUp" className="mb-16">
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              {t('timeline.label')}
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl">
              {t('timeline.title')}
            </h2>
          </AnimatedSection>

          {/* Timeline responsive: vertical en móvil, horizontal en desktop */}
          <div ref={timelineRef} className="relative">
            {/* Desktop: Línea horizontal */}
            <div className="hidden md:block absolute top-0 left-0 right-0 h-px bg-intercasa-dark/10">
              <div
                className="absolute left-0 top-0 h-full bg-intercasa-red transition-all duration-500 ease-out"
                style={{
                  width: hoveredIndex >= 0 ? `${((hoveredIndex + 1) / timelineEvents.length) * 100}%` : '0%'
                }}
              />
            </div>

            {/* Móvil: Timeline vertical */}
            <div className="md:hidden space-y-6">
              {timelineEvents.map((event, index) => (
                <div
                  key={event.year}
                  className="timeline-item group relative flex items-start"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                >
                  {/* Punto y línea vertical */}
                  <div className="flex flex-col items-center mr-4 relative">
                    <div className={`w-4 h-4 rounded-full transition-all duration-300 z-10 ${
                      index <= hoveredIndex
                        ? 'bg-intercasa-red border-2 border-intercasa-red'
                        : 'bg-intercasa-bone border-2 border-intercasa-red'
                    }`} />
                    {/* Línea vertical que conecta con el siguiente punto */}
                    {index < timelineEvents.length - 1 && (
                      <div className={`w-px h-8 mt-2 transition-colors duration-300 ${
                        index < hoveredIndex ? 'bg-intercasa-red' : 'bg-intercasa-dark/10'
                      }`} />
                    )}
                  </div>

                  {/* Card */}
                  <div className={`bg-white p-4 rounded-xl shadow-sm transition-all duration-300 flex-1 ${
                    index <= hoveredIndex ? 'shadow-lg' : 'hover:shadow-lg'
                  }`}>
                    <span className={`block font-baskerville text-2xl font-bold mb-2 transition-colors duration-300 ${
                      index <= hoveredIndex ? 'text-intercasa-red' : 'text-intercasa-red'
                    }`}>
                      {event.year}
                    </span>
                    <h4 className={`font-lato font-bold text-sm mb-1 transition-colors duration-300 ${
                      index <= hoveredIndex ? 'text-intercasa-dark' : 'text-intercasa-dark'
                    }`}>
                      {t(`timeline.events.${event.key}.title`)}
                    </h4>
                    <p className={`font-lato text-xs text-left transition-colors duration-300 ${
                      index <= hoveredIndex ? 'text-intercasa-dark/70' : 'text-intercasa-dark/50'
                    }`}>
                      {t(`timeline.events.${event.key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Grid horizontal */}
            <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-4">
              {timelineEvents.map((event, index) => (
                <div
                  key={event.year}
                  className="timeline-item group relative"
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                >
                  {/* Punto en la línea (solo desktop) */}
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full transition-all duration-300 z-10 ${
                    index <= hoveredIndex
                      ? 'bg-intercasa-red border-2 border-intercasa-red'
                      : 'bg-intercasa-bone border-2 border-intercasa-red'
                  }`} />

                  {/* Card */}
                  <div className="pt-8">
                    <div className={`bg-white p-6 rounded-xl shadow-sm transition-all duration-300 h-full ${
                      index <= hoveredIndex ? 'shadow-lg' : 'hover:shadow-lg'
                    }`}>
                      <span className={`block font-baskerville text-3xl md:text-4xl font-bold mb-2 transition-colors duration-300 ${
                        index <= hoveredIndex ? 'text-intercasa-red' : 'text-intercasa-red'
                      }`}>
                        {event.year}
                      </span>
                      <h4 className={`font-lato font-bold text-sm md:text-base mb-1 transition-colors duration-300 ${
                        index <= hoveredIndex ? 'text-intercasa-dark' : 'text-intercasa-dark'
                      }`}>
                        {t(`timeline.events.${event.key}.title`)}
                      </h4>
                      <p className={`font-lato text-xs md:text-sm text-left transition-colors duration-300 ${
                        index <= hoveredIndex ? 'text-intercasa-dark/70' : 'text-intercasa-dark/50'
                      }`}>
                        {t(`timeline.events.${event.key}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. NUESTRAS INSTALACIONES
        =============================================
      */}
      <section className="bg-white">
        <div className="grid lg:grid-cols-2">
          {/* Imagen */}
          <div className="relative h-[50vh] lg:h-auto lg:min-h-[80vh] overflow-hidden bg-intercasa-bone">
            <div className="absolute inset-0">
              <img 
                src="/images/QUIENES SOMOS/Grimon_10112017__K9A6373.webp"
                alt={t('facilities.imageAlt')}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Overlay con estadística */}
            <div className="absolute bottom-8 left-8 right-8 lg:bottom-12 lg:left-12">
              <AnimatedSection animation="fadeUp">
                <div className="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-lg inline-block">
                  <span className="block font-baskerville text-5xl font-bold text-intercasa-red">+75</span>
                  <span className="font-lato text-intercasa-dark/60 text-sm">{t('facilities.stat')}</span>
                </div>
              </AnimatedSection>
            </div>
          </div>

          {/* Contenido */}
          <div className="flex items-center p-8 md:p-16 lg:p-24">
            <div className="max-w-lg">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  {t('facilities.label')}
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                  {t('facilities.title')}
                </h2>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed mb-6">
                  {t('facilities.text1')}
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fadeUp" delay={0.2}>
                <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed mb-8">
                  {t('facilities.text2')}
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fadeUp" delay={0.3}>
                <LocalizedLink to="/productos">
                  <MagneticButton variant="outline" size="md">
                    {t('facilities.cta')}
                  </MagneticButton>
                </LocalizedLink>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        5. VALORES - Grid limpio
        =============================================
      */}
      <section className="bg-intercasa-dark py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <span className="font-lato text-intercasa-yellow text-sm uppercase tracking-widest mb-4 block">
              {t('values.label')}
            </span>
            <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl">
              {t('values.title')}
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 max-w-5xl mx-auto">
            {valoresData.map((valor, index) => (
              <AnimatedSection
                key={valor.id}
                animation="fadeUp"
                delay={index * 0.05}
              >
                <div className="bg-intercasa-dark p-8 md:p-10 min-h-[280px] md:min-h-[300px] lg:min-h-[320px] group hover:bg-intercasa-dark/80 transition-colors flex flex-col">
                  {/* Icono - alineado arriba */}
                  <span className="block text-3xl text-intercasa-yellow mb-4 group-hover:scale-110 transition-transform inline-block">
                    {valor.icono}
                  </span>
                  
                  {/* Contenido - ocupa el espacio restante */}
                  <div className="flex-1 flex flex-col">
                    {/* Título */}
                    <h3 className="font-baskerville font-bold text-white text-xl mb-2">
                      {t(`values.items.${valor.key}.title`)}
                    </h3>
                    
                    {/* Descripción */}
                    <p className="font-lato text-white/60 text-sm leading-relaxed">
                      {t(`values.items.${valor.key}.description`)}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 
        =============================================
        6. EQUIPO - Layout asimétrico
        =============================================
      */}
      <section className="bg-white">
        <div className="grid lg:grid-cols-2">
          {/* Contenido */}
          <div className="flex items-center p-8 md:p-16 lg:p-24 order-2 lg:order-1">
            <div className="max-w-lg">
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                  {t('team.label')}
                </span>
                <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                  {t('team.title')}
                </h2>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed mb-6">
                  {t('team.text1')}
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fadeUp" delay={0.2}>
                <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed">
                  {t('team.text2')}
                </p>
              </AnimatedSection>
            </div>
          </div>

          {/* Imagen */}
          <div className="relative h-[50vh] lg:h-auto lg:min-h-[70vh] overflow-hidden bg-intercasa-bone order-1 lg:order-2">
            <div className="absolute inset-0">
              <img 
                src="/images/inicio/nosotros-equipo.webp"
                alt={t('team.imageAlt')}
                className="w-full h-full object-cover object-right"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        7. CTA FINAL - Compacto y elegante
        =============================================
      */}
      <section className="bg-intercasa-red py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-6">
                <Trans
                  i18nKey="cta.title"
                  ns="about"
                  components={{ br: <br />, hl: <span className="text-intercasa-yellow" /> }}
                />
              </h2>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="text-center font-lato text-white/80 text-lg md:text-xl leading-relaxed mb-8">
                {t('cta.text')}
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <LocalizedLink to="/productos">
                <MagneticButton variant="secondary" size="lg">
                  {t('cta.button')}
                </MagneticButton>
              </LocalizedLink>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default AboutPageImmersive;
